import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import DramaCard from '@/pages/home/components/DramaCard';
import { dramas, categories, categoryMap } from '@/mocks/dramas';

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const [searchParams] = useSearchParams();
  const searchQ = searchParams.get('q') || '';

  const validType = type && categoryMap[type] ? type : 'guochan';
  const categoryName = categoryMap[validType] || '国产剧';

  const [activeCategory, setActiveCategory] = useState(validType);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    setActiveCategory(validType);
    setCurrentPage(1);
    window.scrollTo(0, 0);
  }, [validType]);

  const filteredDramas = useMemo(() => {
    let result = dramas.filter((d) => d.category === activeCategory);
    if (searchQ) {
      const q = searchQ.toLowerCase();
      result = result.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          d.subCategory.toLowerCase().includes(q) ||
          d.tags.some((t) => t.toLowerCase().includes(q)) ||
          d.cast.some((c) => c.toLowerCase().includes(q))
      );
    }
    return result;
  }, [activeCategory, searchQ]);

  const totalPages = Math.ceil(filteredDramas.length / pageSize);
  const paginatedDramas = filteredDramas.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: `桃子影院 - ${categoryName}全集在线观看`,
              description: `桃子影院${categoryName}全集在线观看，全部免费，高清超清蓝光4K画质，永久免费看。`,
              url: `https://www.taozitv.com/category/${validType}`,
              isPartOf: { '@type': 'WebSite', name: '桃子影院', url: 'https://www.taozitv.com/' },
            }),
          }}
        />

        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 lg:px-8 py-4 bg-background-100/50 border-b border-background-200/30">
          <div className="max-w-[1400px] mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-400 transition-colors whitespace-nowrap">首页</Link>
              <span className="text-foreground-700">/</span>
              <span className="text-foreground-300">{categoryName}</span>
              {searchQ && (
                <>
                  <span className="text-foreground-700">/</span>
                  <span className="text-foreground-300">搜索: {searchQ}</span>
                </>
              )}
            </nav>
          </div>
        </div>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-6 md:py-8 border-b border-background-200/20">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.key}
                  to={`/category/${cat.key}`}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all whitespace-nowrap ${
                    activeCategory === cat.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-400 hover:text-foreground-100 hover:bg-background-200 border border-background-300/30'
                  }`}
                >
                  <span className={`w-4 h-4 flex items-center justify-center ${activeCategory === cat.key ? 'text-background-50' : 'text-foreground-500'}`}>
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Drama Grid */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <div className="max-w-[1400px] mx-auto">
            <div className="mb-6">
              <h1 className="text-xl md:text-2xl font-heading text-foreground-100">
                {searchQ ? `搜索"${searchQ}"的结果` : categoryName}
              </h1>
              <p className="text-foreground-600 text-xs md:text-sm mt-1">
                共找到 <strong className="text-primary-400">{filteredDramas.length}</strong> 部相关剧集
              </p>
            </div>

            {filteredDramas.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">暂无相关剧集</p>
                <Link to="/" className="inline-block mt-4 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
                  返回首页
                </Link>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
                  {paginatedDramas.map((drama) => (
                    <DramaCard key={drama.id} drama={drama} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 border border-background-300/30 text-foreground-400 disabled:opacity-40 disabled:cursor-not-allowed hover:text-foreground-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap ${
                          page === currentPage
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-100 border border-background-300/30 text-foreground-400 hover:text-foreground-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 border border-background-300/30 text-foreground-400 disabled:opacity-40 disabled:cursor-not-allowed hover:text-foreground-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}